import {
  getNameSimilarity,
  normalizePlayerNameForMatching,
  PLAYER_MATCH_CONFIG,
} from './mergeControlPlayerRows';

export const CONTROL_NUMERIC_FIELDS = [
  'score',
  'kills',
  'deaths',
  'assists',
  'impact',
  'damage',
  'captureKills',
  'operatorValue',
];

export const CONTROL_FIELD_LABELS = {
  score: 'Score',
  kills: 'K',
  deaths: 'D',
  assists: 'A',
  impact: 'Impact',
  damage: 'Damage',
  captureKills: 'Capture Kills',
  operatorValue: 'Operator Value',
};

const normalizeRosterOption = (player) => {
  if (typeof player === 'string') {
    return {
      playerId: player,
      playerName: player,
      label: player,
    };
  }

  const playerName = player?.ign || player?.name || player?.Player || player?.playerName || '';
  const playerId = player?.id || player?.playerId || playerName;

  return {
    playerId,
    playerName,
    label: playerName,
  };
};

export const normalizeRosterOptions = (activePlayers = []) => activePlayers
  .map(normalizeRosterOption)
  .filter((player) => player.playerName);

export const findRosterSuggestion = ({
  detectedName,
  rosterOptions,
  config = PLAYER_MATCH_CONFIG,
}) => {
  const normalizedName = normalizePlayerNameForMatching(detectedName);
  if (!normalizedName) return null;

  const exactMatches = rosterOptions.filter((option) => (
    normalizePlayerNameForMatching(option.playerName) === normalizedName
  ));

  if (exactMatches.length === 1) {
    return {
      ...exactMatches[0],
      type: 'exact',
      score: 1,
      candidates: exactMatches.map((option) => ({
        playerId: option.playerId,
        playerName: option.playerName,
        score: 1,
      })),
    };
  }

  if (exactMatches.length > 1) {
    return {
      type: 'ambiguous',
      score: 1,
      candidates: exactMatches.map((option) => ({
        playerId: option.playerId,
        playerName: option.playerName,
        score: 1,
      })),
    };
  }

  const fuzzyCandidates = rosterOptions
    .map((option) => ({
      ...option,
      score: getNameSimilarity(detectedName, option.playerName),
    }))
    .filter((option) => option.score >= config.fuzzyThreshold)
    .sort((a, b) => b.score - a.score);

  if (!fuzzyCandidates.length) return null;

  const [best, secondBest] = fuzzyCandidates;
  if (secondBest && best.score - secondBest.score < config.ambiguityMargin) {
    return {
      type: 'ambiguous',
      score: best.score,
      candidates: fuzzyCandidates.slice(0, 3).map((option) => ({
        playerId: option.playerId,
        playerName: option.playerName,
        score: Math.round(option.score * 1000) / 1000,
      })),
    };
  }

  return {
    playerId: best.playerId,
    playerName: best.playerName,
    label: best.label,
    type: 'fuzzy',
    score: Math.round(best.score * 1000) / 1000,
    candidates: fuzzyCandidates.slice(0, 3).map((option) => ({
      playerId: option.playerId,
      playerName: option.playerName,
      score: Math.round(option.score * 1000) / 1000,
    })),
  };
};

const toDraftNumber = (value) => (
  value === null || value === undefined ? '' : String(value)
);

export const createControlImportDraft = ({
  mergedControlResult,
  activePlayers = [],
} = {}) => {
  const rosterOptions = normalizeRosterOptions(activePlayers);

  return (mergedControlResult?.players || []).map((player, index) => {
    const suggestion = findRosterSuggestion({
      detectedName: player.detectedName,
      rosterOptions,
    });
    const shouldApplySuggestion = suggestion && suggestion.type !== 'ambiguous';

    return {
      rowId: `control-preview-${index}-${normalizePlayerNameForMatching(player.detectedName) || 'player'}`,
      detectedName: player.detectedName || '',
      playerId: shouldApplySuggestion ? suggestion.playerId : null,
      playerName: shouldApplySuggestion ? suggestion.playerName : '',
      operator: '',
      score: toDraftNumber(player.score),
      kills: toDraftNumber(player.kills),
      deaths: toDraftNumber(player.deaths),
      assists: toDraftNumber(player.assists),
      impact: toDraftNumber(player.impact),
      damage: toDraftNumber(player.damage),
      captureKills: toDraftNumber(player.captureKills),
      operatorValue: toDraftNumber(player.operatorValue),
      _sources: player._sources || {},
      _matchConfidence: player._matchConfidence || {},
      _warnings: player._warnings || [],
      _propValueCandidates: player._propValueCandidates || [],
      _rosterSuggestion: suggestion ? {
        type: suggestion.type,
        score: suggestion.score,
        candidates: suggestion.candidates || [],
      } : null,
    };
  });
};

const isValidNonNegativeNumberInput = (value) => {
  if (value === '') return false;
  const numberValue = Number(value);
  return Number.isFinite(numberValue) && numberValue >= 0;
};

export const validateControlImportDraft = (draftPlayers = []) => {
  const errorsByRow = {};
  const selectedCounts = new Map();

  draftPlayers.forEach((row) => {
    if (row.playerId) {
      selectedCounts.set(row.playerId, (selectedCounts.get(row.playerId) || 0) + 1);
    }
  });

  draftPlayers.forEach((row) => {
    const rowErrors = {};

    if (!row.playerId) {
      rowErrors.playerId = 'Player is required.';
    } else if (selectedCounts.get(row.playerId) > 1) {
      rowErrors.playerId = `${row.playerName || 'This player'} is selected more than once.`;
    }

    if (!row.operator) {
      rowErrors.operator = 'Operator is required for Control.';
    }

    CONTROL_NUMERIC_FIELDS.forEach((fieldKey) => {
      if (!isValidNonNegativeNumberInput(row[fieldKey])) {
        rowErrors[fieldKey] = `${CONTROL_FIELD_LABELS[fieldKey]} must be a non-negative number.`;
      }
    });

    if (Object.keys(rowErrors).length) {
      errorsByRow[row.rowId] = rowErrors;
    }
  });

  const completeRows = draftPlayers.filter((row) => (
    row.playerId &&
    row.operator &&
    CONTROL_NUMERIC_FIELDS.every((fieldKey) => isValidNonNegativeNumberInput(row[fieldKey]))
  )).length;
  const mappedPlayers = draftPlayers.filter((row) => row.playerId).length;
  const duplicatePlayers = Array.from(selectedCounts.values()).filter((count) => count > 1).length;
  const fieldsNeedingAttention = Object.values(errorsByRow)
    .reduce((total, rowErrors) => total + Object.keys(rowErrors).length, 0);

  return {
    isValid: draftPlayers.length > 0 && fieldsNeedingAttention === 0,
    errorsByRow,
    summary: {
      totalRows: draftPlayers.length,
      completeRows,
      mappedPlayers,
      duplicatePlayers,
      fieldsNeedingAttention,
    },
  };
};
